import React from "react"
import { useTranslation } from "react-i18next"

import { useAlert, useForm } from "hooks"
import { ErrorResponse, OperatorData } from "models"
import { BikePageProps } from "../../models"

import * as BikeApi from "features/bike/api/BikeApi"

import { Button, Spinner } from "components/Elements"
import { FormElement, formStatusEnum } from "components/Form"
import { jsonHash } from "utils"

export const BikeCoOwner: React.FC<BikePageProps> = ({ bike, refreshBike }) => {
	const {
		formFields,
		formStatus,
		setFormStatus,
		handleChange,
		errors,
		showLocalErrors,
		setShowLocalErrors,
		handleValidation,
		discardChanges,
	} = useForm({
		defaultFormFields: bike,
		defaultFormStatus: formStatusEnum.readonly,
	})

	const { t } = useTranslation()
	const { alert } = useAlert()

	const isLoading = formStatus === formStatusEnum.loading
	const isReadonly = formStatus === formStatusEnum.readonly
	const isReady = formStatus === formStatusEnum.ready

	const formElementCommonProps = {
		showErrors: showLocalErrors,
		onValidate: handleValidation,
		onChange: handleChange,
	}

	const formHasChanged = jsonHash(bike) !== jsonHash(formFields)

	const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault()
		// Validate inputs
		const formReady = !(errors.length > 0)

		if (!formReady) {
			setShowLocalErrors(true)
		} else {
			try {
				// hide error
				setShowLocalErrors(false)
				// prevent form to be submitted again
				setFormStatus(formStatusEnum.loading)

				const coOwner = formFields.operator_data.co_owner

				const bikeCoOwner = {
					...(coOwner.last_name ? { last_name: coOwner.last_name } : {}),
					...(coOwner.first_name ? { first_name: coOwner.first_name } : {}),
					...(coOwner.mail ? { mail: coOwner.mail } : {}),
				}

				const bikeOperatorData: OperatorData = {
					...formFields.operator_data,
					co_owner: bikeCoOwner,
				}

				const updatedBikePayload = {
					bike_id: bike.bike_id,
					row_version: bike.row_version,
					operator_data: bikeOperatorData,
				}

				const { getError } = await BikeApi.updateBike(updatedBikePayload)
				if (getError()) throw getError()

				alert("success", t("info_update", { ns: "form" }))
				await refreshBike()
			} catch (error) {
				console.error(error)
				const errorMsg = (error as ErrorResponse)?.message?.toString()
				alert("error", errorMsg)
			} finally {
				setFormStatus(formStatusEnum.ready)
			}
		}
	}

	return (
		<div className="c-block px-4 py-6 md:mt-8 mb-8">
			<h2 className="c-block__title">{t("co_owner.title", { ns: "bike" })}</h2>
			<p className="c-block__intro">{t("co_owner.introduction", { ns: "bike" })}</p>

			<form>
				<FormElement
					{...formElementCommonProps}
					value={formFields.operator_data?.co_owner?.first_name}
					name="operator_data.co_owner.first_name"
					type="text"
					label={t("firstname", { ns: "user" })}
					className="my-3 mx-2 px-1"
					readonly={isReadonly}
				/>

				<FormElement
					{...formElementCommonProps}
					value={formFields.operator_data?.co_owner?.last_name}
					name="operator_data.co_owner.last_name"
					type="text"
					label={t("lastname", { ns: "user" })}
					className="my-3 mx-2 px-1"
					readonly={isReadonly}
				/>

				<FormElement
					{...formElementCommonProps}
					value={formFields.operator_data?.co_owner?.mail}
					name="operator_data.co_owner.mail"
					type="email"
					label={t("email", { ns: "user" })}
					className="mb-6 mx-2 px-1"
					readonly={isReadonly}
					disabled={isReadonly}
				/>

				{isReady && (
					<div className="flex items-center justify-center md:justify-start">
						<Button
							variant="secondary"
							type="submit"
							onClick={handleSubmit}
							className="mx-1 px-10 py-1 lg:mx-3"
							size="small"
							disabled={!formHasChanged}
						>
							{t("global.validate")}
						</Button>

						<Button
							variant="secondaryBordered"
							type="button"
							onClick={() =>
								discardChanges({ formHasChanged, refreshData: refreshBike })
							}
							className="mx-1 px-10 py-1 lg:mx-3"
							size="small"
						>
							{t("global.cancel")}
						</Button>
					</div>
				)}

				{isReadonly && (
					<Button
						variant="primary"
						type="submit"
						onClick={() => setFormStatus(formStatusEnum.ready)}
						className="px-10 py-1 mx-3"
						size="small"
					>
						{t("global.edit")}
					</Button>
				)}

				{isLoading && (
					<div className="flex justify-center my-8">
						<Spinner color={"var(--primaryColor)"} size={"100px"} />
					</div>
				)}
			</form>
		</div>
	)
}
